import {_} from 'lodash';
import * as ko from "knockout";
import {Automatic} from "./Automatic";
import {ISubmitDataOverride} from "./index";
import {ErrorTracker} from "../../../error.tracker";
import {ICustomButton} from "../../heading/AbstractHeading";
import {IObjectData, IOrganization, ISystemPermission} from "../../../index";
import {Manual} from "./Manual";
import {ObjectTypeId} from "../../../constants";
import {global} from "../../../appContext";
import {viewModel as MutableViewModel} from "../mutable";
import {AbstractSystemPermission} from "./AbstractSystemPermission";

export class Organization {
    readonly toggle: KnockoutObservable<boolean> = ko.observable(false);
    public readonly systemPermissions: KnockoutObservableArray<AbstractSystemPermission> = ko.observableArray<AbstractSystemPermission>([]);
    private readonly customButtons: ICustomButton[] = [];
    departmentId: number = 0;

    constructor(private vm: MutableViewModel, public org: IOrganization, private user: IObjectData | null) {
    }

    init(): Promise<any> {
        return Promise.resolve()
            .then(() => {
                const systemPermissions: AbstractSystemPermission[] = this.org.systempermission.map(sp => sp.manual
                        ? new Manual(this, this.user, sp)
                        : new Automatic(this, this.user, sp)
                    );

                this.systemPermissions(_.sortBy(systemPermissions, (sp: AbstractSystemPermission) => sp.system()?.name.toLocaleLowerCase()));

                if (!this.systemPermissions().every(sp => sp.manual)) {
                    this.customButtons.push({
                        cls: 'fa-plus',
                        onClick: () => {
                            this.addEmptySystemPermission();
                            this.toggle(true);
                        },
                        label: 'Legg til rettighet',
                    });
                }

                const isDeleteable = this.systemPermissions().every(sp => sp.manual);

                if (!this.vm.isOnlyOneOrg && isDeleteable) {
                    this.customButtons.push({
                        cls: 'fa-trash-alt',
                        onClick: () => this.vm.onDeleteOrganizationClick(this),
                        label: 'Fjern organisasjon',
                    });
                }
            });
    }

    /*******************************************************************************************************************
     * Data initialization, validation and check
     ******************************************************************************************************************/

    isValid(errorTracker: ErrorTracker): boolean {
        return this.systemPermissions().reduce((prev, sp) => sp.isValid(errorTracker) && prev, true);
    }

    addEmptySystemPermission() {
        const _sp = {
            comment: '',
            companyid: this.org.organizationid,
            expirydate: '',
            id: 0,
            managerOf: 0,
            managerOfName: '',
            managerOfObjectTypeId: ObjectTypeId.Department,
            manual: true,
            recursive: true,
            role: null!,
            system: null!,
        } as ISystemPermission;
        const sp = new Manual(this, this.user, _sp);
        this.systemPermissions.push(sp)
    }

    addExistingSystemPermission(systemPermission: ISystemPermission) {
        const _sp: ISystemPermission = _.defaultsDeep(systemPermission, {
            comment: '',
            companyid: this.org.organizationid,
            expirydate: '',
            id: 0,
            managerOf: 0,
            manual: true,
            recursive: true,
            role: null!,
            system: null!,
        });
        const sp = new Manual(this, this.user, _sp);
        this.systemPermissions.push(sp)
    }

    isMultipleRoleSameCompanyValid(sp: AbstractSystemPermission): boolean {
        if (sp.system()?.multiplerolesamecompany) {
            return false;
        }

        return _(this.systemPermissions())
            .filter((_sp: AbstractSystemPermission) => _sp.getStatus() != 'deleted' && !_sp.isDeleted() && _sp.system()?.id == sp.system()?.id)
            .value()
            .length > 1;
    }

    isMultipleRoleSameDepartmentValid(sp: AbstractSystemPermission): boolean {
        const system = sp.system();
        if (!system) {
            return true;
        }
        return system.multiplerolesamedepartment || _(this.systemPermissions())
            // we're only interested in comparing instances where system, role and managerOf are all set
            .filter((_sp: AbstractSystemPermission) => _sp.getStatus() != 'deleted' && !!_sp.system() && !!_sp.role() && !!_sp.managerOf())
            .filter((_sp: AbstractSystemPermission) => _sp.system()?.id == sp.system()?.id)
            .filter((_sp: AbstractSystemPermission) => _sp.managerOf() == sp.managerOf())
            .value()
            .length > 1;
    }

    isMultipleEqual(sp: AbstractSystemPermission): boolean {
        if (sp.getStatus() == 'deleted') {
            return false;
        }
        return this.systemPermissions().length > 1 && this.systemPermissions()
            .filter(_sp => _sp.getStatus() != 'deleted')
            .some(_sp => this.isMutableSystemPermissionsEqual(sp, _sp));
    }

    private isMutableSystemPermissionsEqual(spA: AbstractSystemPermission, spB: AbstractSystemPermission): boolean {
        if (spA === spB) {
            return false;
        }
        const a = spA.system()?.id == spB.system()?.id;
        const b = spA.role()?.id == spB.role()?.id;
        const c = spA.managerOf() == spB.managerOf();
        return a && b && c;

    }

    isChanged = ko.pureComputed((): boolean => {
        return this.systemPermissions().some(o => o.isChanged());
    })

    containsNewOverrides(): boolean {
        return !!this.systemPermissions().find(o => o.containsNewOverrides());
    }

    /*******************************************************************************************************************
     * Submit
     ******************************************************************************************************************/

    getSubmitDataPermission(): IOrganization {
        return _.defaults({
            systempermission: _(this.systemPermissions())
                .map((sp: AbstractSystemPermission) => sp.getSubmitDataPermission())
                .flatten()
                .value()
        }, this.org);
    }

    getSubmitDataOverrides(): ISubmitDataOverride[] {
        return this.systemPermissions()
            .map(o => o.getSubmitDataOverride())
            .filter((o): o is ISubmitDataOverride => o != null);
    }

    /*******************************************************************************************************************
     * View helpers
     ******************************************************************************************************************/

    private badge = ko.pureComputed((): number => {
        return this.systemPermissions().length;
    });

    public deleteManual(sp: AbstractSystemPermission) {
        const isLastOne = this.systemPermissions().length == 1;
        if (isLastOne && !this.vm.isOnlyOneOrg) {
            this.vm.onDeleteOrganizationClick(this);
        } else {
            const index = this.systemPermissions().indexOf(sp);
            this.systemPermissions.remove(sp);
            global.snackbarData({
                message: `subject is removed|s:((permission)`,
                undo: () => this.systemPermissions.splice(index, 0, sp),
            });
        }
    }

}
